import React, { memo, useCallback, useMemo } from "react";
import { settings_sidebar_tabs } from "../settings_constants";
import { useGroupChatContext } from "../../../../context/groupchatContext";

/** Careco ACL shape: e.g. { locations: { all: 1 }, facility_staff: { all: 1 } } */
function hasCarecoAllAccess(careco, resourceKey) {
  if (!resourceKey || careco == null || typeof careco !== "object") return false;
  const node = careco[resourceKey];
  if (node == null || typeof node !== "object") return false;
  const all = node.all;
  return all === 1 || all === "1";
}

function SettingsSideBar({ activeTab = "", setActiveTab = () => {}, variant = "vertical" }) {
  const groupChatContext = useGroupChatContext();

  const { authData, userAcl } = useMemo(() => ({
    authData: groupChatContext.authData,
    userAcl: groupChatContext.userAcl,
  }));

  const allowedTabsForProvider = ['report_a_problem']
  const shouldShowTab = useCallback(
    (tab) => {
      const passesRole = tab?.show?.some((role) => {
        if (role === "provider") return !!authData?.is_provider;
        if (role === "nurse") return !authData?.is_provider;
        if (role === "nurse_admin") return !authData?.is_provider && !!authData?.is_facility_admin;
        return false;
      });
      if (!passesRole) return false;

      const isProvider = !!authData?.is_provider;
      const tabSlug = tab?.slug;
      if (!tabSlug || !isProvider) return true;

      if (allowedTabsForProvider.includes(tabSlug)) return true;

      return hasCarecoAllAccess(userAcl?.careco, tabSlug);
    },
    [authData?.is_provider, authData?.is_facility_admin, userAcl?.careco]
  );

  const isHorizontal = variant === "horizontal";
  const filteredTabs = useMemo(
    () => settings_sidebar_tabs.filter((tab) => shouldShowTab(tab)),
    [shouldShowTab]
  );

  return (
    <ul
      className={
        isHorizontal
          ? "flex flex-row items-stretch gap-0 overflow-x-auto overflow-y-hidden w-full min-h-0 border-b border-gray-200 bg-white shadow-sm shrink-0"
          : "w-full h-[calc(100vh-65px)] md:h-[calc(100vh-4rem)] border-r border-gray-200 bg-white shadow-sm flex flex-col shrink-0"
      }
    >
      {filteredTabs.map((tab) => {
        const TabIcon = tab?.Icon;
        return (
          <li
            className={
              isHorizontal
                ? `flex items-center justify-center gap-2 cursor-pointer px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab?.slug
                      ? `${authData?.is_provider ? 'border-teal-500 text-teal-500 bg-teal-50/80' : 'border-violet-600 text-violet-700 bg-violet-50/80'}`
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`
                : `flex items-center h-12 min-h-[3rem] text-sm w-full cursor-pointer gap-3 px-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                    activeTab === tab?.slug
                    ? `${authData?.is_provider ? 'bg-teal-50 text-teal-500 border-l-4 border-l-teal-500 font-medium' : 'bg-violet-50 text-violet-800 border-l-4 border-l-violet-600 font-medium'}`
                      : "text-gray-700 hover:bg-gray-50"
                  }`
            }
            key={tab?.id}
            onClick={() => setActiveTab(tab?.slug)}
          >
            {TabIcon ? <TabIcon {...(tab.iconProps || {})} aria-hidden /> : null}
            {tab?.title}
          </li>
        );
      })}
    </ul>
  );
}

export default memo(SettingsSideBar);
